// content.service.ts
import { api } from './api';
import type { ApiResponse } from '../utils/types';
// import { toast } from 'sonner';


export interface ContentDocument {
    _id: string;
    title: string;
    content: string;
    wordCount: number;
    readingTime: number;
    status: 'draft' | 'published' | 'scheduled';
    createdAt: Date;
    updatedAt: Date;
    lastSavedAt: Date;
    versions: ContentVersion[];
    seoSettings: SEOSettings;
    mediaItems: string[];
    tags: string[];
    category: string;
}

export interface ContentVersion {
    _id: string;
    version: string;
    content: string;
    comment: string;
    timestamp: Date;
    autoSave: boolean;
}

export interface SEOSettings {
    title: string;
    metaDescription: string;
    keywords: string[];
    // slug: string;
}

export interface PublishingSettings {
    platforms: string[];
    scheduleType: 'now' | 'scheduled';
    scheduledDate?: Date;
    status: 'draft' | 'published' | 'scheduled';
}

export interface MediaItem {
    id: string;
    type: 'image' | 'video' | 'audio' | 'document';
    name: string;
    url: string;
    thumbnail?: string;
    size: number;
    uploadedAt: Date;
}

export interface SEOAnalysis {
    score: number;
    recommendations: string[];
    keywordDensity: Record<string, number>;
    readabilityScore: number;
    metaOptimization: {
        titleLength: number;
        descriptionLength: number;
        suggestions: string[];
    };
}

export interface CreateContentDto {
    title: string;
    content: string;
    tags?: string[];
    seoSettings?: Partial<SEOSettings>;
}

export interface UpdateContentDto {
    title?: string;
    content?: string;
    tags?: string[];
    seoSettings?: Partial<SEOSettings>;
}

export interface ContentFilterDto {
    search?: string;
    tags?: string[];
    dateFrom?: string;
    dateTo?: string;
}

export interface ExportContentDto {
    format: 'html' | 'markdown' | 'pdf';
}

// export interface CreateVersionDto {
//     changes: string;
//     body: string;
// }

const API_ENDPOINTS = {
    content: '/content',
    byId: (id: string) => `/content/${id}`,
    versions: (id: string) => `/content/${id}/versions`,
    restoreVersion: (id: string, versionId: string) =>
        `/content/${id}/versions/${versionId}/restore`,
    export: (id: string) => `/content/${id}/export`,
    seo: (id: string) => `/content/${id}/seo-analysis`,
};

export class ContentService {
    async createDocument(document: CreateContentDto): Promise<ContentDocument> {
        try {
            const response = await api.post<ApiResponse<ContentDocument>>(
                API_ENDPOINTS.content,
                document,
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to create content');
            return response.data.data;
        } catch (error) {
            console.error('Create document failed:', error);
            // toast.error('Failed to create content. Please try again.');
            throw error;
        }
    }

    async getDocument(id: string): Promise<ContentDocument> {
        try {
            const response = await api.get<ApiResponse<ContentDocument>>(
                API_ENDPOINTS.byId(id),
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to fetch content');
            return response.data.data;
        } catch (error) {
            console.error('Get document failed:', error);
            // toast.error('Failed to fetch content.');
            throw error;
        }
    }

    async updateDocument(
        id: string,
        updates: UpdateContentDto,
    ): Promise<ContentDocument> {
        try {
            const response = await api.put<ApiResponse<ContentDocument>>(
                API_ENDPOINTS.byId(id),
                updates,
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to update content');
            return response.data.data;
        } catch (error) {
            console.error('Update document failed:', error);
            // toast.error('Failed to update content.');
            throw error;
        }
    }

    async deleteDocument(id: string): Promise<void> {
        try {
            const response = await api.delete<ApiResponse<void>>(
                API_ENDPOINTS.byId(id),
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to delete content');
        } catch (error) {
            console.error('Delete document failed:', error);
            // toast.error('Failed to delete content.');
            throw error;
        }
    }

    async listDocuments(filters?: ContentFilterDto): Promise<ContentDocument[]> {
        try {
            const response = await api.get<ApiResponse<ContentDocument[]>>(
                API_ENDPOINTS.content,
                { params: filters },
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to fetch contents');
            return response.data.data;
        } catch (error) {
            console.error('List documents failed:', error);
            // toast.error('Failed to load contents.');
            throw error;
        }
    }

    async saveVersion(
        documentId: string,
        version: { content: string; autoSave?: boolean ,comment?: string},
    ): Promise<ContentVersion> {
        try {
            const response = await api.post<ApiResponse<ContentVersion>>(
                API_ENDPOINTS.versions(documentId),
                version,
            );

            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to save version');
            }

            return response.data.data;
        } catch (error) {
            console.error('Save version failed:', error);
            throw error;
        }
    }

    async getVersions(documentId: string): Promise<ContentVersion[]> {
        try {
            const response = await api.get<ApiResponse<ContentVersion[]>>(
                API_ENDPOINTS.versions(documentId),
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to fetch versions');
            return response.data.data;
        } catch (error) {
            console.error('Get versions failed:', error);
            // toast.error('Failed to load versions.');
            throw error;
        }
    }

    async restoreVersion(
        documentId: string,
        versionId: string,
    ): Promise<ContentDocument> {
        try {
            const response = await api.post<ApiResponse<ContentDocument>>(
                API_ENDPOINTS.restoreVersion(documentId, versionId),
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to restore version');
            return response.data.data;
        } catch (error) {
            console.error('Restore version failed:', error);
            // toast.error('Failed to restore version.');
            throw error;
        }
    }

    async exportDocument(
        id: string,
        format: ExportContentDto,
    ): Promise<Blob> {
        try {
            const response = await api.post(`${API_ENDPOINTS.export(id)}`, format, {
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            console.error('Export document failed:', error);
            // toast.error('Failed to export document.');
            throw error;
        }
    }

    async analyzeSEO(id: string): Promise<SEOAnalysis> {
        try {
            const response = await api.get<ApiResponse<SEOAnalysis>>(
                API_ENDPOINTS.seo(id),
            );
            if (!response.data.success)
                throw new Error(response.data.message || 'Failed to analyze SEO');
            return response.data.data;
        } catch (error) {
            console.error('SEO analysis failed:', error);
            // toast.error('Failed to analyze SEO.');
            throw error;
        }
    }
}

export const contentService = new ContentService();